import { useCallback, useMemo } from 'react'

import { F0Box, type DataDownloadCanvasContent } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'

import { useDataDownloadView } from './DataDownloadProvider'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RecordType = Record<string, any>

const PER_PAGE = 25

export type DataDownloadContentProps = {
  content: DataDownloadCanvasContent
  refreshKey?: number
}

export function DataDownloadContent({ content }: DataDownloadContentProps) {
  const { dataset } = content
  const { columns, rows, columnLabels } = dataset

  const sortings = useMemo(
    () => Object.fromEntries(columns.map((col) => [col, { label: columnLabels?.[col] ?? col }])),
    [columns, columnLabels]
  )

  const dataAdapter = useMemo(
    () => ({
      paginationType: 'pages' as const,
      perPage: PER_PAGE,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fetchData: (options: any) => {
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- treating rows as records
        let filtered = [...rows] as RecordType[]

        // Search across all columns
        if (options.search?.trim()) {
          // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
          const term = (options.search as string).toLowerCase()
          filtered = filtered.filter((row) =>
            columns.some((col) => {
              const val = row[col]
              return val != null && String(val).toLowerCase().includes(term)
            })
          )
        }

        // Sort
        if (options.sortings?.length) {
          for (const { field, order } of [
            // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
            ...(options.sortings as { field: string; order: string }[]),
          ].reverse()) {
            filtered.sort((a, b) => {
              const aVal = a[field]
              const bVal = b[field]
              if (aVal == null && bVal == null) return 0
              if (aVal == null) return 1
              if (bVal == null) return -1
              if (typeof aVal === 'number' && typeof bVal === 'number') {
                return order === 'asc' ? aVal - bVal : bVal - aVal
              }
              const cmp = String(aVal).localeCompare(String(bVal))
              return order === 'asc' ? cmp : -cmp
            })
          }
        }

        // Paginate
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
        const page = (options.pagination?.currentPage as number) ?? 1
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
        const perPage = (options.pagination?.perPage as number) ?? PER_PAGE
        const start = (page - 1) * perPage
        const records = filtered.slice(start, start + perPage)

        return {
          records,
          total: filtered.length,
          perPage,
          type: 'pages' as const,
          currentPage: page,
          pagesCount: Math.ceil(filtered.length / perPage),
        }
      },
    }),
    [rows, columns]
  )

  const { setViewState } = useDataDownloadView()

  const handleStateChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => {
      const tableSettings = state.settings?.visualization?.table
      const hiddenColumns =
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
        (tableSettings?.hidden as string[] | undefined) ?? []
      const columnOrder =
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
        (tableSettings?.order as string[] | undefined) ?? []
      const sortings = state.sortings ? [state.sortings].flat().filter(Boolean) : []
      setViewState({
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- shaped by OneDataCollection
        search: state.search as string | undefined,
        sortings,
        hiddenColumns,
        columnOrder,
      })
    },
    [setViewState]
  )

  const source = useDataCollectionSource<RecordType>(
    {
      dataAdapter: dataAdapter,
      sortings,
      search: { enabled: true },
    },
    [rows, columns, sortings]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          allowColumnHiding: true,
          allowColumnReordering: true,
          columns: columns.map((col) => ({
            id: col,
            label: columnLabels?.[col] ?? col,
            sorting: col,
            render: (item: RecordType) => {
              const value = item[col]
              return value == null ? '' : String(value)
            },
          })),
        },
      },
    ],
    [columns, columnLabels]
  )

  return (
    <F0Box height="full" grow paddingTop="xl">
      <OneDataCollection
        fullHeight
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-type-assertion/no-type-assertion -- OneDataCollection generics are heavy; relax at the boundary
        source={source as any}
        visualizations={visualizations}
        storage={false}
        onStateChange={handleStateChange}
      />
    </F0Box>
  )
}
